<template>
  <div class="meeting-room-page">
    <div>
      <h1>Hub Room</h1>
      <div class="monthNavWrap">
        <button
          @click="prevWeek"
          class="monthNav"
          :disabled="isPrevWeekDisabled"
        >
          ◀︎
        </button>
        <h2 v-html="currentWeekLabel"></h2>
        <button
          @click="nextWeek"
          class="monthNav"
          :disabled="isNextWeekDisabled"
        >
          ▶︎
        </button>
      </div>
      <div class="weekendSwc">
        <span>Weekends</span>
        <input
          type="checkbox"
          id="hide-toggle"
          v-model="hideWeekends"
          class="weekendCheck"
        />
        <label for="hide-toggle" class="weekendLabel"></label>
      </div>
      <div class="tableWrap">
          <table class="seatCalendar">
            <thead>
              <tr>
                <th></th>
                <th v-for="date in dates" :key="date.toISOString()">
                  {{ formatDateEN(date) }}
                </th>
              </tr>
            </thead>
            <tbody>
              <tr
                v-for="(time, idx) in times"
                :key="time"
                :class="{
                  saturday: isSaturday(time),
                  sunday: isSunday(time),
                }"
                style="height: 40px"
              >
                <td>
                  <span v-if="idx % 2 === 0">{{ time }}</span>
                </td>
                <td
                  v-for="date in dates"
                  :key="date.toISOString()"
                  @click="openModal(date, time)"
                  :class="[
                    'seat-cell',
                    getCellClass(date, time),
                    {
                      saturday: date.getDay() === 6,
                      sunday: date.getDay() === 0,
                      now: isNowCell(date, time),
                    },
                  ]"
                  :ref="(el) => { if (isNowCell(date, time)) nowCells.push(el); }"
                >
                  <template
                    v-for="r in getReservationsForCell(date, time)"
                    :key="r.id"
                  >
                    <div>
                      <template v-if="isReservationCenter(r, time)">
                        <p>{{ r.title }}</p>
                        <p class="organiserName"> {{ r.user_name }}</p>
                      </template>
                      <template v-else> &nbsp; </template>
                    </div>
                  </template>
                </td>
              </tr>
            </tbody>
          </table>
      </div>
    </div>

    <MeetingRoomReservationModal
      :visible="modalOpen"
      :date="selectedDate"
      :time="selectedTime"
      :room-id="selectedRoomId"
      @close="modalOpen = false"
      @reservation-created="handleReservationCreated"
    />
    <CancelConfirmationModal
      :visible="showCancelConfirmation"
      :message="`Do you want to cancel your reservation for ${cancelingReservation?.title}?`"
      @confirm="confirmCancellation"
      @cancel="cancelCancellation"
    />
  </div>
</template>

<script setup>
import { ref, onMounted, computed, watch, nextTick } from "vue";
import api, {
  setAuthToken,
  fetchMeetingRooms,
  fetchMeetingReservations,
  fetchTeams,
  deleteMeetingReservation,
} from "@/services/api";
import MeetingRoomReservationModal from "../components/MeetingRoomReservationModal.vue";
import CancelConfirmationModal from "../components/CancelConfirmationModal.vue";
import timeSyncService from "@/services/timeSyncService";

const rooms = ref([]);
const reservations = ref([]);
const selectedRoomId = ref(null);
const modalOpen = ref(false);
const selectedDate = ref("");
const selectedTime = ref("");
const showCancelConfirmation = ref(false);
const cancelingReservation = ref(null);
const nowCells = ref([]); // now クラスを持つセルを格納する配列

const hideWeekends = ref(false);
const currentWeekStart = ref(getStartOfWeek(timeSyncService.now()));

// Calculate min and max display dates
const minDisplayDate = computed(() => {
  const d = timeSyncService.now();
  d.setMonth(d.getMonth() - 2); // 2 months ago
  d.setDate(1); // First day of the month
  return d;
});

const maxDisplayDate = computed(() => {
  const d = timeSyncService.now();
  d.setFullYear(d.getFullYear() + 1); // 1 year from now
  d.setMonth(11); // December
  d.setDate(31); // Last day of the month
  return d;
});

const isPrevWeekDisabled = computed(() => {
  const newDate = new Date(currentWeekStart.value);
  newDate.setDate(newDate.getDate() - 7);
  return newDate < minDisplayDate.value;
});

const isNextWeekDisabled = computed(() => {
  const newDate = new Date(currentWeekStart.value);
  newDate.setDate(newDate.getDate() + 7);
  return newDate > maxDisplayDate.value;
});

function getStartOfWeek(date) {
  const d = new Date(date);
  const day = d.getDay();
  // Adjust Sunday to 6, Monday to 0 with (day + 6) % 7
  const diff = d.getDate() - ((day + 6) % 7);
  d.setDate(diff);
  d.setHours(0, 0, 0, 0);
  return d;
}

const dates = computed(() => {
  const list = [];
  let d = new Date(currentWeekStart.value);
  for (let i = 0; i < 7; i++) {
    if (!hideWeekends.value || (d.getDay() !== 0 && d.getDay() !== 6)) {
      list.push(new Date(d));
    }
    d.setDate(d.getDate() + 1);
  }
  return list;
});

const currentWeekLabel = computed(() => {
  const start = new Date(currentWeekStart.value);
  const end = new Date(currentWeekStart.value);
  end.setDate(end.getDate() + 6);
  // Wrap year with span
  return `${start.getDate()}/${
    start.getMonth() + 1
  }/<span class='year'>${start.getFullYear()}</span> - ${end.getDate()}/${
    end.getMonth() + 1
  }/<span class='year'>${end.getFullYear()}</span>`;
});

function prevWeek() {
  const newDate = new Date(currentWeekStart.value);
  newDate.setDate(newDate.getDate() - 7);
  if (newDate < minDisplayDate.value) {
    return; // Do nothing if out of bounds
  }
  currentWeekStart.value = newDate;
  fetchReservations();
}
function nextWeek() {
  const newDate = new Date(currentWeekStart.value);
  newDate.setDate(newDate.getDate() + 7);
  if (newDate > maxDisplayDate.value) {
    return; // Do nothing if out of bounds
  }
  currentWeekStart.value = newDate;
  fetchReservations();
}

watch([currentWeekStart, hideWeekends], () => {});

const times = generateTimeList();

function generateTimeList() {
  const list = [];
  for (let hour = 8; hour <= 19; hour++) {
    const hh = hour.toString().padStart(2, "0");
    list.push(`${hh}:00`);
    list.push(`${hh}:15`);
    list.push(`${hh}:30`);
    list.push(`${hh}:45`);
  }
  return list;
}

async function openModal(date, time) {
  const d = new Date(date);
  const year = d.getFullYear();
  const month = (d.getMonth() + 1).toString().padStart(2, "0");
  const day = d.getDate().toString().padStart(2, "0");
  const dateString = `${year}-${month}-${day}`;
  const dt = new Date(`${dateString}T${time}:00`); // This is the time of the cell being clicked

  const now = timeSyncService.now(); // Get the current synchronized time
  const nowDateTime = new Date(
    now.getFullYear(),
    now.getMonth(),
    now.getDate(),
    now.getHours(),
    now.getMinutes(),
    0,
    0
  );

  const cellReservations = getReservationsForCell(date, time);
  const currentUserId = localStorage.getItem("userId");

  if (cellReservations.length > 0) {
    const reservation = cellReservations[0]; // Get the first reservation in this cell
    if (reservation.user === parseInt(currentUserId)) {
      // It's my reservation.
      const reservationEnd = new Date(reservation.end_datetime);
      // If my reservation's end time is in the past, do not show cancel modal.
      if (reservationEnd < nowDateTime) {
        return;
      }
      // Otherwise, show cancel confirmation modal for my current/future reservation.
      cancelingReservation.value = reservation;
      showCancelConfirmation.value = true;
    } else {
      // It's another user's reservation. Do nothing.
      console.log("This reservation belongs to another user.");
      return; // Explicitly return to prevent opening new reservation modal
    }
  } else {
    // No reservation in this cell.
    // If the clicked cell's time is in the past, do nothing.
    if (dt < nowDateTime) {
      return;
    }
    // Otherwise, open the new reservation modal.
    selectedDate.value = dateString;
    selectedTime.value = time;
    modalOpen.value = true;
  }
}

async function confirmCancellation() {
  try {
    await deleteMeetingReservation(cancelingReservation.value.id);
    fetchReservations(); // Update reservation list
  } catch (error) {
    console.error("Error cancelling reservation:", error);
    alert("Failed to cancel reservation.");
  }
  showCancelConfirmation.value = false;
  cancelingReservation.value = null;
}

function cancelCancellation() {
  showCancelConfirmation.value = false;
  cancelingReservation.value = null;
}

function getReservationsForCell(date, time) {
  if (!selectedRoomId.value) return [];

  const year = date.getFullYear();
  const month = (date.getMonth() + 1).toString().padStart(2, "0");
  const day = date.getDate().toString().padStart(2, "0");
  const dateString = `${year}-${month}-${day}`;
  const dt = new Date(`${dateString}T${time}:00`);

  return reservations.value.filter((r) => {
    const start = new Date(r.start_datetime);
    const end = new Date(r.end_datetime);
    return r.room === selectedRoomId.value && dt >= start && dt < end;
  });
}

function isReservationCenter(reservation, time) {
  const start = new Date(reservation.start_datetime);
  const end = new Date(reservation.end_datetime);

  const startTimeStr = start.toTimeString().slice(0, 5);
  // Use the last displayed slot (end - 15m) so reservations ending at 20:00
  // still map to a valid time in the grid.
  const lastSlotTime = new Date(end);
  lastSlotTime.setMinutes(lastSlotTime.getMinutes() - 15);
  const endTimeStr = lastSlotTime.toTimeString().slice(0, 5);

  const startIdx = times.indexOf(startTimeStr);
  const endIdx = times.indexOf(endTimeStr);
  if (startIdx === -1 || endIdx === -1) return false;

  // span = 終了インデックス - 開始インデックス
  const span = endIdx - startIdx;
  if (span < 0) return false;
  if (span === 0) return times[startIdx] === time;

  let centerIdx;
  if (span % 2 === 0) {
    centerIdx = startIdx + span / 2 - 1;
  } else {
    centerIdx = startIdx + Math.floor(span / 2);
  }

  return times[centerIdx] === time;
}

function getReservationPosition(reservation, time) {
  const start = new Date(reservation.start_datetime);
  const end = new Date(reservation.end_datetime);
  const startTimeStr = start.toTimeString().slice(0, 5);
  const endTimeStr = end.toTimeString().slice(0, 5);

  if (time === startTimeStr) return "start";
  if (time === endTimeStr) return "end";
  if (time > startTimeStr && time < endTimeStr) return "middle";
  return "";
}
function getCellClass(date, time) {
  const cellReservations = getReservationsForCell(date, time);
  if (!cellReservations.length) {
    // Gray out past time slots (even without reservations)
    if (isPastTime(date, time)) return "past";
    return "";
  }
  // pick the first reservation in this slot
  const r = cellReservations[0];
  let baseClass = getReservationStyle(r, time);
  // Add past class if reservation end time has passed
  if (isReservationPast(r)) baseClass += " past";
  // Add mine class if it's your own reservation
  const myId = Number(localStorage.getItem("userId"));
  if (r.user === myId) baseClass += " mine";
  return baseClass;
}

function isPastTime(date, time) {
  // date: Date object, time: "HH:mm"
  const now = timeSyncService.now();
  const y = date.getFullYear();
  const m = date.getMonth();
  const d = date.getDate();
  const [hh, mm] = time.split(":").map(Number);
  const slotEnd = new Date(y, m, d, hh, mm + 15, 0, 0); // 15分単位の終了
  return slotEnd < now;
}

function isReservationPast(reservation) {
  if (!reservation) return false;
  const end = new Date(reservation.end_datetime);
  const now = timeSyncService.now();
  return end < now;
}
function getReservationStyle(reservation, time) {
  const start = new Date(reservation.start_datetime);
  const end = new Date(reservation.end_datetime);

  // canonical strings for the first (and only) slot
  const startStr = start.toTimeString().slice(0, 5);

  // compute the *displayed* last slot (15m before true end)
  const prevEnd = new Date(end);
  prevEnd.setMinutes(prevEnd.getMinutes() - 15);
  const prevEndStr = prevEnd.toTimeString().slice(0, 5);

  // duration in ms
  const durationMs = end - start;
  const fifteenMin = 15 * 60 * 1000;

  // 1) single‐cell reservation?
  if (durationMs === fifteenMin) {
    // only one slot, so if this cell is that slot, mark it single
    return time === startStr ? "reservedSingle reservedMTR" : "";
  }

  // 2) multi‐slot reservation
  if (time === startStr) {
    return "reservedMTRFirst reservedMTR";
  } else if (time === prevEndStr) {
    return "reservedMTRLast reservedMTR";
  } else if (time > startStr && time < prevEndStr) {
    return "reservedMTRin reservedMTR";
  } else {
    return "";
  }
}

async function fetchRooms() {
  const res = await fetchMeetingRooms();
  rooms.value = res.data;
  if (res.data.length > 0) {
    selectedRoomId.value = res.data[0].id;
    fetchReservations();
  }
}

async function fetchReservations() {
  if (!selectedRoomId.value) return;

  const start = new Date(currentWeekStart.value);
  const end = new Date(currentWeekStart.value);
  end.setDate(end.getDate() + 6);

  const startStr = start.toISOString().split("T")[0];
  const endStr = end.toISOString().split("T")[0];

  const res = await api.get(
    `/meeting/reservations/?start_date=${startStr}&end_date=${endStr}`
  );
  reservations.value = res.data;
}

function handleReservationCreated() {
  modalOpen.value = false;
  fetchReservations();
}

onMounted(async () => {
  const token = sessionStorage.getItem("token");
  if (token) {
    setAuthToken(token);
  } else {
    console.error("Authentication token not found. Please log in.");
  }
  await fetchRooms();
  nextTick(() => {
    const targetCell = nowCells.value[0];
    if (targetCell) {
      targetCell.scrollIntoView({ behavior: "smooth", block: "center" });
    }
  });
});

async function fetchTeamsList() {
  const res = await fetchTeams();
  rooms.value = res.data;
}

function formatDateEN(date) {
  const d = date instanceof Date ? date : new Date(date);
  const day = d.getDate();
  const weekday = d.toLocaleDateString("en-GB", { weekday: "short" });
  return `${day} ${weekday}`;
}

function isSaturday(time) {
  // time: "HH:mm" but needs to be checked against each date in the dates array
  // Since the first column (time label) has no day of the week information, it is more accurate to check each td.
  return false; // If checking per td, this is not used
}
function isSunday(time) {
  return false;
}

function isNowCell(date, time) {
  const now = timeSyncService.now();
  if (
    now.getFullYear() !== date.getFullYear() ||
    now.getMonth() !== date.getMonth() ||
    now.getDate() !== date.getDate()
  )
    return false;
  const [hh, mm] = time.split(":").map(Number);
  const cellStart = new Date(
    date.getFullYear(),
    date.getMonth(),
    date.getDate(),
    hh,
    mm,
    0,
    0
  );
  const cellEnd = new Date(cellStart.getTime() + 15 * 60 * 1000);
  return now >= cellStart && now < cellEnd;
}
</script>
<style>
& .year {
  font-size: 0.8em;
}
</style>
<style scoped>
h1 {
  font-size: 1.5em;
  color: var(--blue);
}
h2 {
  letter-spacing: 1.2px;
  font-size: 1.2em;
  width: 220px;
}
.monthNavWrap {
  margin-bottom: 1rem;
  display: flex;
  align-items: center;
  gap: 0.2rem;
}
.monthNav {
  padding: 0.3em 0.5em;
  font-size: 14px;
  background: none;
  border: none;
  cursor: pointer;
}
.weekendSwc {
  margin-bottom: 1rem;
  display: flex;
  align-items: center;
  gap: 0.5rem;
}
.weekendCheck {
  margin-left: 0.5em;
}
.weekendLabel {
  margin-left: 0.2em;
}
.seatCalendar {
  width: 100%;
  border-collapse: collapse;
}
.tableWrap{
  overflow: auto;
  height: 70vh;
}

thead {
    z-index: 2;
    position: sticky;
    top: 0;
}
tbody{
  position: relative;
  z-index: 1;
}
th {
  text-align: center;
  color: white;
  font-size: 13px;
  height: 2rem;
  padding: 8px;
  background: var(--blue);
  &:first-child {
    border-top-left-radius: 10px;
  }
  &:last-child {
    border-top-right-radius: 10px;
  }
}
td {
  padding: 8px;
  border: 1px dashed #0757a280;
  text-align: center;
  & input,
  select {
    border: none;
  }
  &:first-child {
    border-left: none;
  }
  &:last-child {
    border-right: none;
  }
  &.reservedMTR {
    & > div {
      background: var(--lightBlue);
      height: 100%;
      align-content: center;
      color: var(--midLightBlue);
    }
  }
  &.mine {
    & > div {
      background: var(--grad90);
      color: white;
    }
  }
  &.past {
    background: #f8f8f8;
    & > div {
      background: #e9e9e9;
      color: gray;
    }
  }
  &.reservedMTRFirst {
    border-bottom: none;
    padding-top: 10px;
    padding-bottom: 0px;

    & > div {
      padding-top: 10px;
      padding-bottom: 0;
      border-top-left-radius: 10px;
      border-top-right-radius: 10px;
    }
  }
  &.reservedMTRin {
    border-top: none;
    border-bottom: none;
    padding-top: 0px;
    padding-bottom: 0px;

    & > div {
      padding-top: 0px;
      padding-bottom: 0px;
    }
  }
  &.reservedMTRLast {
    border-top: none;
    padding-top: 0px;
    padding-bottom: 10px;

    & > div {
      padding-top: 0px;
      padding-bottom: 10px;
      border-bottom-left-radius: 10px;
      border-bottom-right-radius: 10px;
    }
  }
  &.reservedSingle {
    & > div {
      border-radius: 10px;
    }
  }
  & p {
    margin: 0;
  }
  & p.organiserName {
    font-size: 12px;
    
  }
}
tr {
  position: relative;
  &:first-child > td {
    border-top: none;
    & > span {
      display: none;
    }
  }
  & > td:first-child {
    border-left: none;
    color: var(--blue);
    font-weight: 600;
    text-align: left;
    border: none;
    position: absolute;
    top: -1rem;
  }
  &.saturday,
  &.sunday,
  &.uk-holiday {
    background: #e9e9e9;
  }
}
.error {
  color: red;
  margin-top: 1rem;
}
th:first-child {
  font-size: 12px;
  width: 35px;
}
.seat-cell {
  position: relative;
  height: 40px;
  width: 90px;
}
.year {
  font-size: 0.8em;
  color: #888;
}
.seat-cell.mine,
td.mine,
.reservedMTR.mine,
.reservedMTRFirst.mine,
.reservedMTRin.mine,
.reservedMTRLast.mine {
  color: #0656a2 !important;
  font-weight: bold;
}
.seat-cell.saturday,
.seat-cell.sunday {
  background: #e9e9e9 !important;
  color: gray !important;
}
.seat-cell.now {
  background: var(--gradYel);
}

@media (max-width: 768px) {
  tr {
    & > td:first-child {
      font-size: 12px;
      min-width: 30px;
    }
  }
  .seatCalendar {
    min-width: max-content;
  }
}
</style>
